import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ToastMessage, ToastType } from '../components/feedback/Toast';

export type MessagesContextValue = {
    messages: ToastMessage[];
    addMessage: (message: string, type?: ToastType) => void;
    addError: (message: string) => void;
    addSuccess: (message: string) => void;
    addInfo: (message: string) => void;
    addWarning: (message: string) => void;
    removeMessage: (id: string) => void;
    clearAll: () => void;
};

const MessagesContext = createContext<MessagesContextValue | undefined>(undefined);

export const MessagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [messages, setMessages] = useState<ToastMessage[]>([]);

    const removeMessage = useCallback((id: string) => {
        setMessages((prev) => prev.filter((msg) => msg.id !== id));
    }, []);

    const addMessage = useCallback((message: string, type: ToastType = 'info') => {
        const id = `${Date.now()}-${Math.random()}`;
        setMessages((prev) => [...prev, { id, message, type }]);
    }, []);

    const addError = useCallback((message: string) => {
        addMessage(message, 'error');
    }, [addMessage]);

    const addSuccess = useCallback((message: string) => {
        addMessage(message, 'success');
    }, [addMessage]);

    const addInfo = useCallback((message: string) => {
        addMessage(message, 'info');
    }, [addMessage]);

    const addWarning = useCallback((message: string) => {
        addMessage(message, 'warning');
    }, [addMessage]);

    const clearAll = useCallback(() => {
        setMessages([]);
    }, []);

    const value = useMemo(
        () => ({
            messages,
            addMessage,
            addError,
            addSuccess,
            addInfo,
            addWarning,
            removeMessage,
            clearAll,
        }),
        [messages, addMessage, addError, addSuccess, addInfo, addWarning, removeMessage, clearAll]
    );

    return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
};

export function useMessages(): MessagesContextValue {
    const ctx = useContext(MessagesContext);
    if (!ctx) throw new Error('useMessages must be used within MessagesProvider');
    return ctx;
}

